import React from 'react'
import '@/styles/admin-modal.css'

interface AdminLayoutProps {
  children: React.ReactNode
  currentPage: string
  currentUser: {
    id: string
    name: string
    role: string
    email: string
  }
  onNavigate: (page: string) => void
  onLogout: () => void
}

interface MenuItem {
  id: string
  label: string
  icon: React.ReactNode
  description?: string
  dividerBefore?: boolean
  deprecated?: boolean
}

export function AdminLayout({ 
  children, 
  currentPage, 
  currentUser, 
  onNavigate, 
  onLogout 
}: AdminLayoutProps) {
  const [sidebarOpen, setSidebarOpen] = React.useState(true)
  const [currentTime, setCurrentTime] = React.useState(new Date())
  const [showLogoutModal, setShowLogoutModal] = React.useState(false)

  React.useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date())
    }, 1000) // 1秒ごとに更新

    return () => clearInterval(timer)
  }, [])

  const handleLogoutClick = () => {
    setShowLogoutModal(true)
  }

  const handleLogoutConfirm = () => {
    setShowLogoutModal(false)
    onLogout()
  }

  const handleLogoutCancel = () => {
    setShowLogoutModal(false)
  }

  // ページ階層の定義
  const pageHierarchy: Record<string, { parent?: string; label: string }> = {
    'users': { label: 'ユーザ管理' },
    'user-detail': { parent: 'users', label: 'ユーザ詳細' },
    'user-management': { label: 'ユーザ管理' },
    'procedure-types': { label: '申請種別管理' },
    'procedure-type-detail': { parent: 'procedure-types', label: '申請種別詳細' },
    'workflows-list': { label: 'ステータスフロー管理' },
    'workflow-detail': { parent: 'workflows-list', label: 'ステータスフロー詳細' },
    'approval-routes-list': { label: '承認ルート管理' },
    'approval-route-detail': { parent: 'approval-routes-list', label: '承認ルート詳細' },
    'bindings-list': { label: '申請バインド管理' },
    'binding-new': { parent: 'bindings-list', label: '新規バインド作成' },
    'binding-detail': { parent: 'bindings-list', label: '申請バインド詳細' },
    'form-version-list': { label: 'フォーム管理' },
    'form-version-detail': { parent: 'form-version-list', label: 'フォームバージョン詳細' }
  }

  // パンくずリストを生成
  const getBreadcrumbs = () => {
    const breadcrumbs: Array<{ id: string; label: string }> = []
    let currentId: string | undefined = currentPage

    while (currentId && pageHierarchy[currentId]) {
      breadcrumbs.unshift({
        id: currentId,
        label: pageHierarchy[currentId].label
      })
      currentId = pageHierarchy[currentId].parent
    }

    return breadcrumbs
  }

  const menuItems: MenuItem[] = [
    {
      id: 'users',
      label: 'ユーザ管理',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
        </svg>
      ),
      description: 'システムユーザーの管理'
    },
    {
      id: 'procedure-types',
      label: '申請種別管理',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
        </svg>
      ),
      description: '申請種別の管理'
    },
    {
      id: 'form-version-list',
      label: 'フォーム管理',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
      ),
      description: 'フォーム定義の管理'
    },
    {
      id: 'workflows-list',
      label: 'ステータスフロー管理',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
        </svg>
      ),
      description: 'ワークフロー定義の管理'
    },
    {
      id: 'approval-routes-list',
      label: '承認ルート管理',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
        </svg>
      ),
      description: '承認ルート定義の管理'
    },
    {
      id: 'bindings-list',
      label: '申請バインド管理',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
        </svg>
      ),
      description: '新ワークフローのバインド管理'
    }
  ]

  return (
    <div className="min-h-screen bg-white">
      {/* ヘッダー */}
      <header className="bg-white fixed w-full top-0 z-50" style={{ borderBottom: '1px solid #e5e7eb' }}>
        <div className="h-16 px-6 flex items-center justify-between">
          <div className="flex items-center">
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="p-2 rounded-md text-gray-600 hover:text-gray-900 hover:bg-gray-100 focus:outline-none focus:ring-1 focus:ring-blue-500 transition-colors"
              title="メニュー開閉"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
            <div className="ml-6 pl-6" style={{ borderLeft: '1px solid #e5e7eb' }}>
              <h1 className="text-xl font-bold text-gray-900">Flowive</h1>
              <p className="text-xs text-gray-600 mt-0.5">システム管理者画面</p>
            </div>
          </div>
          
          <div className="flex items-center space-x-6">
            <div className="flex items-center text-sm text-gray-600">
              <svg className="w-4 h-4 mr-2 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <time dateTime={currentTime.toISOString()}>
                {currentTime.toLocaleDateString('ja-JP')} {currentTime.toLocaleTimeString('ja-JP', { hour: '2-digit', minute: '2-digit' })}
              </time>
            </div>
            <div className="flex items-center space-x-3 pl-6" style={{ borderLeft: '1px solid #e5e7eb' }}>
              <div className="text-right">
                <div className="text-sm font-medium text-gray-900">{currentUser.name}</div>
                <div className="text-xs text-gray-600">システム管理者</div>
              </div>
              <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center border-2 border-blue-700">
                <span className="text-white font-semibold text-sm" style={{ textShadow: '0 1px 2px rgba(0, 0, 0, 0.3)' }}>
                  {currentUser.name.charAt(0)}
                </span>
              </div>
            </div>
          </div>
        </div>
      </header>

      <div className="flex pt-16 relative">
        {/* オーバーレイ（モバイル用） */}
        {sidebarOpen && (
          <div 
            className="fixed inset-0 bg-black bg-opacity-50 z-30 lg:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}
        
        {/* サイドバー */}
        <aside 
          className={`${
            sidebarOpen ? 'translate-x-0' : '-translate-x-full'
          } w-72 bg-white transition-transform duration-300 fixed left-0 top-16 h-[calc(100vh-4rem)] shadow-md z-40`}
          style={{ borderRight: '1px solid #e5e7eb' }}
        >
          <div className="h-full flex flex-col">
            <div className="px-6 py-4" style={{ borderBottom: '1px solid #e5e7eb', minHeight: '56px', display: 'flex', alignItems: 'center' }}>
              <h2 className="text-sm font-semibold text-gray-600 uppercase tracking-wider">システム管理メニュー</h2>
            </div>
            
            <nav className="flex-1 px-4 py-4 overflow-y-auto">
              <ul className="space-y-1" style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                {menuItems.map((item) => (
                  <li
                    key={item.id}
                    style={item.dividerBefore ? { marginTop: '2rem', paddingTop: '1rem', borderTop: '1px solid #e5e7eb' } : {}}
                  >
                    <button
                      onClick={() => onNavigate(item.id)}
                      className={`w-full text-left px-4 py-3 rounded-md transition-all duration-150 flex items-start group ${
                        currentPage === item.id
                          ? 'bg-blue-50 text-blue-700 shadow-sm'
                          : 'text-gray-700 hover:bg-gray-50'
                      }`}
                      style={item.deprecated ? { backgroundColor: '#f3f4f6', opacity: 0.6 } : {}}
                            >
                      <div className="flex items-start w-full">
                        <div className={`mr-3 mt-0.5 flex-shrink-0 ${
                          currentPage === item.id ? 'text-blue-600' : 'text-gray-400 group-hover:text-gray-600'
                        }`}>
                          {item.icon}
                        </div>
                        <div className="flex-1">
                          <div className={`font-medium ${
                            currentPage === item.id ? 'text-blue-700' : 'text-gray-900'
                          }`}>
                            {item.label}
                          </div>
                          {item.description && (
                            <div className="text-xs text-gray-500 mt-1">{item.description}</div>
                          )}
                        </div>
                        {currentPage === item.id && (
                          <div className="ml-2 w-1 h-12 bg-blue-600 rounded-full -mr-4" />
                        )}
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            </nav>
            
            <div className="p-4 bg-gray-50" style={{ borderTop: '1px solid #e5e7eb' }}>
              <button
                onClick={handleLogoutClick}
                className="logout-button w-full flex items-center justify-center px-4 py-2.5 text-sm font-medium text-gray-700 bg-white rounded-md hover:bg-gray-50 transition-colors duration-150"
                style={{ border: '1px solid #d1d5db' }}
              >
                <style>{`
                  .logout-button:hover svg {
                    color: rgb(55 65 81);
                  }
                `}</style>
                <svg className="w-4 h-4 mr-2 text-gray-500 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                </svg>
                ログアウト
              </button>
            </div>
          </div>
        </aside>

        {/* メインコンテンツ */}
        <main className={`flex-1 transition-all duration-300 ${
          sidebarOpen ? 'ml-72' : 'ml-0'
        }`}>
          {/* パンくずリスト */}
          <nav className="px-8 py-4" style={{ borderBottom: '1px solid #e5e7eb', backgroundColor: '#f9fafb', minHeight: '56px', display: 'flex', alignItems: 'center' }} aria-label="パンくずリスト">
            <ol className="flex items-center text-sm" style={{ listStyle: 'none' }}>
              <li className="flex items-center">
                <button
                  className="text-gray-500 hover:text-blue-600 hover:bg-gray-100 px-2 py-0 rounded transition-all duration-150 border-none bg-transparent cursor-pointer"
                  onClick={() => onNavigate('bindings-list')}
                  title="管理画面ホームに戻る"
                >
                  管理画面
                </button>
                {getBreadcrumbs().map((breadcrumb, index) => (
                  <React.Fragment key={breadcrumb.id}>
                    <svg className="w-5 h-5 text-gray-400" style={{ marginLeft: '0.75rem', marginRight: '0.75rem' }} fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                    </svg>
                    {index === getBreadcrumbs().length - 1 ? (
                      <span className="text-gray-900 font-medium">
                        {breadcrumb.label}
                      </span>
                    ) : (
                      <button
                        className="text-gray-500 hover:text-blue-600 hover:bg-gray-100 px-2 py-0 rounded transition-all duration-150 border-none bg-transparent cursor-pointer"
                        onClick={() => onNavigate(breadcrumb.id)}
                      >
                        {breadcrumb.label}
                      </button>
                    )}
                  </React.Fragment>
                ))}
              </li>
            </ol>
          </nav>
          {children}
        </main>
      </div>

      {/* ログアウト確認モーダル */}
      {showLogoutModal && (
        <div className="admin-modal-overlay">
          {/* オーバーレイ */}
          <div 
            className="admin-modal-backdrop"
            onClick={handleLogoutCancel}
          />
          
          {/* モーダル本体 */}
          <div className="admin-modal-container">
            <div className="admin-modal-content">
              {/* コンテンツ */}
              <div className="admin-modal-body">
                <div className="admin-modal-icon-wrapper">
                  <div className="admin-modal-icon">
                    <svg fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 9V5.25A2.25 2.25 0 0013.5 3h-6a2.25 2.25 0 00-2.25 2.25v13.5A2.25 2.25 0 007.5 21h6a2.25 2.25 0 002.25-2.25V15m3 0l3-3m0 0l-3-3m3 3H9" />
                    </svg>
                  </div>
                  <div className="admin-modal-text">
                    <p className="admin-modal-title">
                      ログアウトしますか？
                    </p>
                    <p className="admin-modal-description">
                      現在の作業内容は保存されています。<br />
                      再度ログインすることで作業を続けることができます。
                    </p>
                  </div>
                </div>
              </div>
              
              {/* フッター */}
              <div className="admin-modal-footer">
                <button
                  type="button"
                  className="admin-modal-confirm"
                  onClick={handleLogoutConfirm}
                >
                  ログアウト
                </button>
                <button
                  type="button"
                  className="admin-modal-cancel"
                  onClick={handleLogoutCancel}
                >
                  キャンセル
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}